import React, { createRef, useEffect, useState } from 'react';
import { DropDown, NumericLabel, SectionLabel } from '../../components/common';
import AudioPlayer from '../../components/common/audio-player-slider';
import AuthenticatedVideo from '../../components/common/authenticated-video';
import SaveButton from '../../containers/clients/switch-bar-button';
import { Dialog } from '../ui';
import { ListenerAudio } from '../../hooks/useListenerAudio';
import { useChunkedUpload } from '../../hooks/useChunkedUpload';
import { listenerRejectTemplateMessages, SystemMessage, useSystemMessages } from '../../hooks/useComms';
import { Tag, TagExtended } from '../../hooks/useTags';
import { PeerType, useListenerProfile } from '../../hooks/usePeers';
import { convertObjectToParams } from '../common/create-params';
import { ROUTE_PATH } from '../../routes/paths';
import { getListenerProfileUrl } from '../../containers/listener-reviewers/common/listener-review-constants';
import { toast } from 'react-toastify';

import { useQueryClient } from '@tanstack/react-query';

export const audioGrades = {
  All: 'all',
  Ungraded: 'ungraded',
  Approved: 'approved',
  Rejected: 'rejected',
};

export interface UnsavedChanges {
  modalVisible: boolean;
  isDirty: boolean;
  isTopicsDirty: boolean;
  promptId?: number;
}

export const defaultErrorMessages = {
  message: '',
  description: '',
};

export interface ExperiencePayloadData {
  grade: string;
  systemMessageGroup: string;
  messageId: string | number;
  message: string;
  video_id?: string;
  transcription: string;
  topicsUpdated: boolean;
  gradeUpdated: boolean;
}

export const defaultExperienceData = {
  grade: 'rejected',
  systemMessageGroup: 'listener_message_rejected',
  messageId: 'none',
  message: '',
  transcription: '',
  video_id: '',
  topicsUpdated: false,
  gradeUpdated: false,
};

export const defaultUnsavedChanges = {
  modalVisible: false,
  isDirty: false,
  isTopicsDirty: false,
};

export interface ExperienceTileProps {
  experience: ListenerAudio;
  handleSelectedExperience: (experience?: ListenerAudio) => void;
  listenerId?: number;
  selectedExperience: ListenerAudio | undefined;
  setUnsavedChanges: (value: React.SetStateAction<UnsavedChanges>) => void;
  unsavedChanges: UnsavedChanges;
}

export const ExperienceTile: React.FC<ExperienceTileProps> = ({
  experience: experience,
  handleSelectedExperience,
  listenerId,
  selectedExperience,
  setUnsavedChanges,
  unsavedChanges,
}) => {
  return (
    <div
      data-testid={`experience-${experience.id}`}
      key={experience.id}
      className={` ${
        selectedExperience?.id === experience.id ? 'bg-blue-primary text-white' : 'text-gray-dark bg-gray-200'
      } my-3 flex h-36 cursor-pointer items-start justify-between rounded-2xl px-7 py-4`}
      onClick={() => {
        unsavedChanges.isDirty
          ? setUnsavedChanges((prev: UnsavedChanges) => ({
              ...prev,
              modalVisible: true,
              promptId: experience.id,
            }))
          : handleSelectedExperience(experience);
      }}
    >
      <span>
        <div className="w-90 flex flex-col space-y-1 truncate">
          <span>
            <p className="text-xxs uppercase text-neutral-600">Title</p>
            <p className="font-bold">{experience.title ? experience.title : <i>No experience title</i>}</p>
          </span>
          {!listenerId && (
            // Right now, v2/listener/id/audio doesn't return the following data, so exclude on that page
            <>
              <span>
                <p className="text-xxs uppercase text-neutral-600">Listener #{experience.listener_role_id}</p>
                <p className="font-bold">
                  {experience.listener_role?.user?.first_name || experience?.listener_role?.user?.last_name
                    ? `${experience.listener_role?.user?.first_name} ${experience.listener_role.user.last_name}`
                    : 'No name'}
                </p>
              </span>
              <span>
                <p className="text-xxs uppercase text-neutral-600">Email</p>
                <p className="font-bold">{experience.listener_role?.user?.email_address || ''}</p>
              </span>
            </>
          )}
        </div>
      </span>
      <span className="flex h-full flex-col justify-between">
        <span>
          <p className="text-xxs uppercase text-neutral-600">Audio</p>
          <p className="font-bold">#{experience.id}</p>
        </span>
        <button
          className={
            'rounded-md border px-4 py-1 text-base font-bold ' +
            (selectedExperience?.id === experience.id
              ? ' border-white'
              : experience?.message_quality === 7
                ? ' border-gray-dark'
                : '') +
            (experience?.message_quality === 3
              ? 'bg-green text-white'
              : experience?.message_quality === 7
                ? ' bg-transparent'
                : ' bg-red text-white')
          }
        >
          {experience?.message_quality === 3 ? 'Approved' : experience?.message_quality === 7 ? 'Ungraded' : 'Rejected'}
        </button>
      </span>
    </div>
  );
};

export interface ExperienceDetailProps {
  selectedExperience: ListenerAudio;
  setIsDirty: (isDirty: boolean) => void;
  setTopicsDirty: (isDirty: boolean) => void;
  experienceData: ExperiencePayloadData;
  setExperienceData: React.Dispatch<React.SetStateAction<any>>;
  submitGrade: () => void;
  errors: any;
  topicTags: TagExtended[];
  setTags: React.Dispatch<React.SetStateAction<TagExtended[]>>;
  isDirty: boolean;
  submit: () => void;
  experienceType: 'audio' | 'video';
  fromPeerProfile?: boolean;
}

export const ExperienceDetails: React.FC<ExperienceDetailProps> = ({
  selectedExperience: experience,
  setIsDirty,
  setTopicsDirty,
  experienceData,
  setExperienceData,
  submitGrade,
  errors,
  topicTags,
  setTags,
  isDirty,
  submit,
  experienceType,
  fromPeerProfile,
}) => {
  const { data: systemMessages } = useSystemMessages();
  const { data: listenerDetails } = useListenerProfile(experience.listener_role_id);

  const queryClient = useQueryClient();
  const peerType =
    listenerDetails &&
    [PeerType.ONBOARDING_PEER, PeerType.REJECTED_PEER, PeerType.ACTIVE_PEER].includes(listenerDetails.state)
      ? 'peer'
      : 'listener';
  const [tagsModalOpen, setTagsModalOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const { upload: chunkedUpload, progress: uploadProgress, cancel: cancelUpload, isUploading } = useChunkedUpload();
  const fileInputRef = React.createRef<HTMLInputElement>();

  const handleFileUpload = () => {
    fileInputRef.current?.click();
  };

  const validateVideoFile = (file: File): Promise<{ valid: boolean; error?: string }> => {
    return new Promise((resolve) => {
      const allowedTypes = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-msvideo'];
      if (!allowedTypes.includes(file.type) && !file.type.startsWith('video/')) {
        resolve({ valid: false, error: 'Please select a valid video file (MP4, MOV, or WebM)' });
        return;
      }

      const video = document.createElement('video');
      video.preload = 'metadata';

      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        const duration = video.duration;

        if (duration < 45) {
          resolve({ valid: false, error: `Video is too short (${duration.toFixed(0)}s). Minimum is 45 seconds` });
        } else if (duration > 180) {
          const minutes = (duration / 60).toFixed(1);
          resolve({ valid: false, error: `Video is too long (${minutes} min). Maximum is 3 minutes` });
        } else {
          resolve({ valid: true });
        }
      };

      video.onerror = () => {
        resolve({ valid: false, error: 'Could not read video file. It may be corrupted or in an unsupported format' });
      };

      video.src = URL.createObjectURL(file);
    });
  };

  const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = event.target.files?.[0];
      if (!file) return;

      const validation = await validateVideoFile(file);
      if (!validation.valid) {
        toast.error(validation.error);
        return;
      }

      await chunkedUpload(file, {
        listener_audio_id: experience.id,
        listener_role_id: experience.listener_role_id,
        title: experience.title || undefined,
        upload_source: 'SB_VIDEO_LINK',
      });

      toast.success('Video uploaded successfully');
      queryClient.invalidateQueries(['listener_audio']);
      queryClient.invalidateQueries(['listener_audio_browse']);
    } catch (error: any) {
      if (error?.message !== 'Upload cancelled') {
        toast.error(error?.message || 'Failed to upload video');
      }
    } finally {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const experienceRejectedMessages = listenerRejectTemplateMessages(systemMessages);
  const url =
    (peerType === 'peer' ? ROUTE_PATH.PEERS_PROFILE : ROUTE_PATH.LISTENERS_PROFILE) +
    convertObjectToParams({
      listenerRoleId: listenerDetails?.id,
      listenerId: listenerDetails?.user?.id,
      listenerType: peerType === 'peer' ? 'peer' : 'listener',
      listenerName: listenerDetails?.user?.first_name || '',
    });

  const sanitizeFilename = (filename: string): string => {
    // Remove or replace characters that are not safe for filenames
    return filename
      .replace(/[<>:"/\\|?*]/g, '_')
      .replace(/\s+/g, '_')
      .replace(/_{2,}/g, '_')
      .trim();
  };

  const getFileExtension = (fileUrl: string): string => {
    // Try to extract extension from URL first
    const urlMatch = fileUrl.match(/\.([a-zA-Z0-9]+)(?:\?|$)/);
    if (urlMatch) {
      return urlMatch[1];
    }
    // Fallback to mp4 for video
    return 'mp4';
  };

  const getVideoMimeType = (url: string): string => {
    const ext = url.match(/\.([a-zA-Z0-9]+)(?:\?|#|$)/)?.[1]?.toLowerCase() ?? 'mp4';
    const map: Record<string, string> = {
      mp4: 'video/mp4',
      mov: 'video/quicktime',
      webm: 'video/webm',
      avi: 'video/x-msvideo',
      mkv: 'video/x-matroska',
    };
    return map[ext] ?? 'video/mp4';
  };

  const vimeoWatchHref =
    experience.video_url || (experienceData.video_id ? `https://vimeo.com/${experienceData.video_id}` : '');

  const handleVideoDownload = async () => {
    if (!experience.file_url) {
      return;
    }

    setDownloading(true);
    setDownloadProgress(0);
    const fileUrl = experience.file_url;

    const urlParts = fileUrl.split('/');
    const filenameFromUrl = urlParts[urlParts.length - 1]?.split('?')[0] || 'file';

    const baseFilename = experience.title || experience.ai_title || filenameFromUrl;
    const sanitizedFilename = sanitizeFilename(baseFilename);
    const extension = getFileExtension(fileUrl);
    const downloadFilename = `${sanitizedFilename}.${extension}`;

    try {
      const domain = process.env.REACT_APP_DOMAIN_NAME || window.location.origin;
      const endpointPath = `/video/${filenameFromUrl}`;
      const fullUrl = `${domain}${endpointPath}`;

      const response = await fetch(fullUrl);
      if (!response.ok) throw new Error(`Failed to download file: ${response.statusText}`);
      const contentLength = Number(response.headers.get('Content-Length') || 0);
      const reader = response.body!.getReader();

      if ('showSaveFilePicker' in window) {
        const handle = await (window as any).showSaveFilePicker({ suggestedName: downloadFilename });
        const writable = await handle.createWritable();
        let received = 0;
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          await writable.write(value);
          received += value.byteLength;
          if (contentLength > 0) {
            setDownloadProgress(Math.round((received / contentLength) * 100));
          }
        }
        await writable.close();
      } else {
        const chunks: Uint8Array[] = [];
        let received = 0;
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          received += value.byteLength;
          if (contentLength > 0) {
            setDownloadProgress(Math.round((received / contentLength) * 100));
          }
        }
        const blob = new Blob(chunks);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = downloadFilename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
      toast.success('Video downloaded successfully');
    } catch (error: any) {
      if (error?.name === 'AbortError') return;
      console.error('Error downloading file:', error);
      toast.error('Failed to download video. Please try again.');
    } finally {
      setDownloading(false);
      setDownloadProgress(0);
    }
  };

  const mapAudioToExperienceData = (experience: ListenerAudio) => {
    setExperienceData({
      grade:
        experience?.message_quality === 3 ? 'approved' : experience?.message_quality === 7 ? 'ungraded' : 'rejected',
      systemMessageGroup: 'listener_message_rejected',
      messageId: 'none',
      message: '',
      ai_summary: experience?.ai_summary || '',
      transcription: experience?.transcription || '',
      video_id: experience?.video_id || null,
      topicsUpdated: false,
      gradeUpdated: false,
    });
  };

  const handleIsDirty = (isTopics = false) => {
    const type = isTopics ? 'topicsUpdated' : 'gradeUpdated';
    if (!experienceData[type]) {
      setExperienceData((prev: any) => ({
        ...prev,
        [type]: true,
      }));
      if (isTopics) {
        setTopicsDirty(true);
        !experienceData.topicsUpdated && setExperienceData((prev: any) => ({ ...prev, topicsUpdated: true }));
      }
    }
    setIsDirty(true);
  };

  const toggleHasTag = (hasTag: boolean, id: number) => {
    if (!experienceData.topicsUpdated) {
      setExperienceData((prev: any) => ({ ...prev, topicsUpdated: true }));
    }
    const updatedTags = topicTags.map((item: TagExtended) => {
      if (item.id === id) {
        return { ...item, hasTag };
      } else {
        return item;
      }
    });
    setTags(updatedTags);
    handleIsDirty(true);
  };

  useEffect(() => {
    mapAudioToExperienceData(experience);
  }, [experience]);

  return (
    <>
      <div className="h-auto rounded-3xl border-2 border-gray-200 px-5">
        <span className="inline-flex w-5/6">
          <span className="text-gray-dark py-4 text-left text-xl font-bold">
            <p className={`text-left font-bold`}>{experience.title ? experience.title : <i>No experience title</i>}</p>
            <a
              href={getListenerProfileUrl(experience.listener_role_id, experience.listener_role?.user?.id)}
              target="_blank"
              rel="noopener noreferrer"
              className="cursor-pointer truncate text-left font-bold"
            >
              by {experience.listener_role?.user?.first_name || ''}
            </a>
            <a
              href={getListenerProfileUrl(experience.listener_role_id, experience.listener_role?.user?.id)}
              target="_blank"
              rel="noopener noreferrer"
              className="cursor-pointer text-left font-bold"
            >
              {' '}
              aka{' '}
              {experience.listener_role?.user?.display_name
                ? experience.listener_role?.user?.display_name
                : experience.listener_role?.user?.email_address}
            </a>
          </span>
        </span>
        <span className="text-gray-dark inline-flex h-max w-1/6 flex-col items-center gap-2 py-4 font-bold">
          <SaveButton
            data-testid="experience-review-save-button"
            disable={!isDirty}
            onSave={() => {
              submit();
            }}
          />
          <p>#{experience.id}</p>
        </span>
        {listenerDetails?.is_active && (
          <a
            className="bg-blue-primary mb-2 flex h-6 w-32 items-center justify-center rounded-xl px-2 text-xs font-bold text-white"
            href={`${process.env.REACT_APP_MWA_LINK}/peer-details?listenerId=${experience.listener_role_id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Peer MWA Page
          </a>
        )}
        {listenerDetails && !fromPeerProfile && (
          <a
            className="bg-blue-primary mb-2 flex h-6 w-28 items-center justify-center rounded-xl px-2 text-xs font-bold text-white"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Peer SB Page
          </a>
        )}
        {experienceType === 'video' && experience.file_url && (
          <div className="mb-4 flex flex-col gap-2">
            <div className="flex gap-2">
              <button
                data-testid="download-video-button"
                onClick={handleVideoDownload}
                disabled={downloading}
                className="bg-blue-primary rounded-full px-3 py-2 text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {downloading ? `Downloading ${downloadProgress}%` : 'Download Video'}
              </button>
              <button
                data-testid="upload-video-button"
                onClick={isUploading ? cancelUpload : handleFileUpload}
                className={`rounded-full px-3 py-2 text-white ${
                  isUploading ? 'bg-red' : 'bg-blue-primary'
                } disabled:cursor-not-allowed disabled:opacity-50`}
              >
                {isUploading ? 'Cancel Upload' : 'Upload Video'}
              </button>
              <input ref={fileInputRef} type="file" accept="video/*" className="hidden" onChange={onFileChange} />
            </div>
            {isUploading && (
              <div className="flex items-center gap-3">
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-200">
                  <div
                    className="bg-blue-primary h-full transition-all duration-300"
                    style={{ width: `${uploadProgress.percent}%` }}
                  />
                </div>
                <span className="text-sm text-gray-600">
                  {uploadProgress.phase === 'uploading'
                    ? `${uploadProgress.percent}% (${uploadProgress.chunksUploaded}/${uploadProgress.totalChunks} chunks)`
                    : uploadProgress.phase === 'completing'
                      ? 'Processing...'
                      : 'Preparing...'}
                </span>
              </div>
            )}
          </div>
        )}
        {experienceType === 'audio' ? (
          <>
            <AudioPlayer audioLength={experience.duration} audioUrl={experience.file_url} />
          </>
        ) : (
          <div className="space-y-4">
            {experience.file_url ? (
              <div className="space-y-1">
                <p className="text-xxs font-semibold uppercase text-neutral-600">Uploaded file</p>
                <AuthenticatedVideo
                  fileUrl={experience.file_url}
                  mimeType={getVideoMimeType(experience.file_url)}
                  className="max-h-64 w-full rounded"
                />
              </div>
            ) : null}
            {experience.video_url || experienceData.video_id ? (
              <div className="space-y-2">
                <p className="text-xxs font-semibold uppercase text-neutral-600">Vimeo</p>
                {experienceData.video_id && /^\d{10}$/.test(String(experienceData.video_id)) && (
                  <div className="relative w-full max-w-2xl overflow-hidden rounded" style={{ paddingTop: '56.25%' }}>
                    <iframe
                      src={`https://player.vimeo.com/video/${experienceData.video_id}`}
                      className="absolute inset-0 h-full w-full"
                      allow="autoplay; fullscreen; picture-in-picture"
                      allowFullScreen
                      title="Vimeo player"
                    />
                  </div>
                )}
                {vimeoWatchHref ? (
                  <a
                    data-testid="vimeo-url"
                    href={vimeoWatchHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 underline hover:text-blue-700"
                  >
                    Watch on Vimeo
                  </a>
                ) : null}
                <div className="flex items-center gap-2">
                  <label className="w-24">Vimeo ID</label>
                  <input
                    data-testid="vimeo-id"
                    type="text"
                    className="text-gray-dark bg-gray-background-light w-full rounded-md text-sm font-extrabold leading-4 focus:ring-transparent"
                    value={experienceData.video_id}
                    onChange={(event) => {
                      handleIsDirty();
                      setExperienceData((prev: any) => ({
                        ...prev,
                        video_id: event.target.value,
                      }));
                    }}
                  />
                </div>
              </div>
            ) : null}
          </div>
        )}
        <div className="mt-5 flex items-center justify-start">
          <label className="mr-2">Grade</label>
          <div className="w-48">
            <DropDown
              data-testid="grade-dropdown"
              Label={''}
              value={experienceData.grade}
              setValue={(value) => {
                value !== 'ungraded' && handleIsDirty();
                setExperienceData((prev: any) => ({
                  ...prev,
                  grade: value,
                  sytemMessageGroup: value === 'rejected' ? 'listener_message_rejected' : value,
                  gradeUpdated: value === 'ungraded' ? false : true,
                }));
              }}
            >
              {experience?.message_quality === 7 && <option value={'ungraded'}>Ungraded</option>}
              <option value="rejected">Rejected</option>
              <option value="approved">Approved</option>
            </DropDown>
          </div>
        </div>
        {experienceData.grade === 'rejected' && (
          <div className="flex w-full items-center justify-start">
            <label className="mr-2">Message to Listener</label>
            <DropDown
              data-testid="message-dropdown"
              Label={''}
              value={experienceData.messageId}
              setValue={(value) => {
                handleIsDirty();
                const comm = experienceRejectedMessages?.find((item: any) => item.id === Number(value));
                setExperienceData((prev: any) => ({
                  ...prev,
                  messageId: value,
                  message: comm ? comm.message : prev.message,
                }));
              }}
            >
              <option value={'none'}>Do NOT Message Listener</option>
              {experienceRejectedMessages.map((item: SystemMessage) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </DropDown>
          </div>
        )}
        {experienceData.grade === 'rejected' && (
          <div className="my-2 w-full">
            <p className="mb-2"> Message </p>
            <textarea
              data-testid="rejected-message"
              className="text-gray-dark bg-gray-background-light h-36 w-full rounded-md px-3 py-2 text-sm font-extrabold leading-4 focus:outline-none"
              value={experienceData?.message}
              onChange={(evt) => {
                setExperienceData((prev: any) => ({
                  ...prev,
                  message: evt.target.value,
                }));
              }}
            />
            {errors.message?.length > 0 && <p className="text-red">{errors.message}</p>}
          </div>
        )}
        <button
          data-testid="submit-grade"
          className={
            'mt-4 rounded-full px-3 py-2 ' +
            (experienceData.gradeUpdated
              ? 'bg-blue-primary text-white'
              : 'bg-gray-background-light text-gray-dark cursor-not-allowed')
          }
          disabled={!experienceData.gradeUpdated || experienceData.grade === '7'}
          onClick={submitGrade}
        >
          Submit Grade
        </button>
        <div>
          <SectionLabel Label="Transcription" />
          <div className="pt-4">
            <textarea
              data-testid="experience-review-transcription"
              className="text-gray-dark bg-gray-background-light h-36 w-full rounded-md px-3 py-2 text-sm font-extrabold leading-4 focus:outline-none"
              value={experienceData?.transcription}
              onChange={(event) => {
                handleIsDirty(true);
                setExperienceData((prev: any) => ({
                  ...prev,
                  transcription: event.target.value,
                }));
              }}
            />
            <div className="text-gray-dark text-right text-sm">{experienceData?.transcription?.length} characters</div>
          </div>
        </div>
        <div>
          <SectionLabel Label="AI Summary" />
          <div className="pb-6 pt-2">
            {experience.ai_summary ? experience.ai_summary : 'No AI summary generated at this time.'}
          </div>
        </div>
        <div className="flex justify-between">
          <p className="text-gray-dark py-2 text-left font-bold">Topic Tags</p>
          <button
            data-testid="add-tag"
            className="text-blue-primary py-2 text-right font-bold"
            onClick={() => {
              setTagsModalOpen(true);
            }}
          >
            + Add
          </button>
        </div>
        <hr className="bg-gray-dark mb-2"></hr>
        <div className="-ml-2 w-full flex-wrap space-x-2 space-y-2 pb-4 text-sm">
          {topicTags?.length > 0 &&
            topicTags
              .filter((tag: TagExtended) => tag.hasTag && !tag.is_required)
              .map((tag: TagExtended, index: number) => {
                return (
                  <button
                    data-testid={`tag-${tag.id}`}
                    className={`bg-blue-primary rounded-md text-sm font-bold text-white ${
                      index === 0 ? 'ml-2 mt-2' : ''
                    }`}
                  >
                    <div className="flex px-3 py-2">
                      <div className="grow pr-2">{tag.name}</div>
                      <div
                        data-testid={`remove-tag-${tag.id}`}
                        className="grow-0"
                        onClick={() => {
                          toggleHasTag(false, tag.id);
                        }}
                      >
                        x
                      </div>
                    </div>
                  </button>
                );
              })}
        </div>
        {experience?.opensearch_tag_ids && (
          <>
            <hr className="mt-4" />
            <div className="flex flex-col">
              <p className="text-gray-dark py-2 text-left font-bold">AI Recommended Tags</p>
              <div className="flex gap-4 py-4">
                {experience?.opensearch_tag_ids?.map((id: number) => {
                  return (
                    <div className="rounded-md bg-gray-600 px-3 py-2 text-sm font-bold text-white">
                      {topicTags.find((tag: Tag) => tag.id === id)?.name}
                    </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
        <div className="mt-2 flex justify-start">
          <div className="h-36 w-1/4">
            <SectionLabel Label={'Plays'} />
            <NumericLabel numericValue={experience?.total_playbacks?.toString() || '0'} label={''} />
          </div>
          <div className="h-36 w-1/4">
            <SectionLabel Label={'Favorites'} />
            <NumericLabel numericValue={experience?.total_favorites?.toString() || '0'} label={''} />
          </div>
        </div>
      </div>
      <Dialog open={tagsModalOpen} onClose={() => setTagsModalOpen(false)}>
        <div className="inline-block max-w-xl transform overflow-hidden rounded-lg bg-white text-left align-bottom transition-all">
          <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                <h3 className="mb-3 text-lg font-bold leading-6 text-gray-900" id="modal-title">
                  Add Listener Tags
                </h3>
                <div className="-ml-2 w-full flex-wrap space-x-2 space-y-2 pb-4 text-sm">
                  {topicTags?.length > 0 &&
                    topicTags
                      .filter((tag: TagExtended) => !tag.is_required)
                      .sort((tag1: TagExtended, tag2: TagExtended) => tag1.name.localeCompare(tag2.name))
                      .map((tag: TagExtended, index: number) => {
                        return (
                          <button
                            data-testid={`add-tag-${tag.id}`}
                            className={` ${
                              tag.hasTag ? 'bg-blue-primary text-white' : 'bg-gray-background-light'
                            } rounded-md text-sm font-bold ${index === 0 ? 'ml-2 mt-2' : ' '}`}
                            onClick={() => {
                              toggleHasTag(!tag.hasTag, tag.id);
                            }}
                          >
                            <div className="flex px-3 py-2">
                              <div className="grow pr-2">{tag.name}</div>
                            </div>
                          </button>
                        );
                      })}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:flex sm:justify-center sm:px-6">
            <button
              data-testid="close-tags-modal"
              type="button"
              className={'rounded-full bg-gray-400 px-5 py-1 font-medium text-white'}
              onClick={() => {
                setTagsModalOpen(false);
              }}
            >
              Close
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
};
